
import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, FileText } from 'lucide-react';

const LESSON_TYPES = [
    'Simulator lesson',
    'Road lesson'
];

const BookingModal = ({ isOpen, onClose, onSave, students = [], instructorBranchId = null }) => {
    const [selectedStudent, setSelectedStudent] = useState('');
    const [lessonType, setLessonType] = useState(LESSON_TYPES[0]);
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [startTime, setStartTime] = useState('09:00');
    const [endTime, setEndTime] = useState('09:30');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Auto-calculate end time when start time changes (30 mins later)
    useEffect(() => {
        if (startTime) {
            const [hours, minutes] = startTime.split(':').map(Number);
            const totalMinutes = hours * 60 + minutes + 30;
            const newHours = Math.floor(totalMinutes / 60) % 24;
            const newMinutes = totalMinutes % 60;
            const calculatedEndTime = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
            setEndTime(calculatedEndTime);
        }
    }, [startTime]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            // Find the selected student to get their customer_id
            const student = students.find(s => String(s.id) === String(selectedStudent));
            const lessonData = {
                student_id: String(selectedStudent),
                customer_id: student?.customer_id || String(selectedStudent),
                lesson_type: lessonType,
                start_time: `${date} ${startTime}:00`,
                end_time: `${date} ${endTime}:00`,
                branch_id: instructorBranchId || null,
                notes: notes || null,
                lesson_status: 'scheduled'
            };
            await onSave(lessonData);
            // Reset form
            setSelectedStudent('');
            setLessonType(LESSON_TYPES[0]);
            setNotes('');
            onClose();
        } catch (error) {
            alert('Error booking lesson: ' + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '10px',
        borderRadius: '6px',
        border: '1px solid #e5e7eb',
        color: '#1F2937',        // Ensure dark text
        backgroundColor: '#ffffff' // Ensure white background
    };
    const labelStyle = { display: 'block', fontSize: '14px', marginBottom: '8px', fontWeight: '500' };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div className="bg-white rounded-xl shadow-2xl" style={{ width: '550px', maxHeight: '90vh', overflowY: 'auto', padding: '28px' }}>
                <div className="flex justify-between items-center mb-6">
                    <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Book a Lesson</h2>
                    <button onClick={onClose} style={{ background: 'none', color: '#6B7280', cursor: 'pointer' }}>
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Student */}
                    <div>
                        <label style={labelStyle}>Student *</label>
                        <select value={selectedStudent} onChange={e => setSelectedStudent(e.target.value)} required style={inputStyle}>
                            <option value="">Select a student...</option>
                            {students.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Lesson Type */}
                    <div>
                        <label style={labelStyle}>Lesson Type *</label>
                        <select value={lessonType} onChange={e => setLessonType(e.target.value)} style={inputStyle}>
                            {LESSON_TYPES.map(t => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>

                    {/* Date & Time */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}><Calendar size={14} style={{ display: 'inline', marginRight: '4px' }} />Date *</label>
                            <input type="date" value={date} onChange={e => setDate(e.target.value)} required style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}><Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />Start *</label>
                            <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}><Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />End *</label>
                            <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} required style={inputStyle} />
                        </div>
                    </div>

                    {/* Notes */}
                    <div>
                        <label style={labelStyle}><FileText size={14} style={{ display: 'inline', marginRight: '4px' }} />Notes</label>
                        <textarea
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Any additional notes for this lesson..."
                            rows={3}
                            style={{ ...inputStyle, resize: 'vertical' }}
                        />
                    </div>

                    {/* Actions */}
                    <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: 'white', cursor: 'pointer' }}
                        >Cancel</button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            style={{ padding: '10px 20px', borderRadius: '8px', backgroundColor: '#4f46e5', color: 'white', fontWeight: 'bold', cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1 }}
                        >{isSubmitting ? 'Booking...' : 'Confirm Booking'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BookingModal;
